import { ChartFragment } from 'models/api-response';
import { ChartActionsComponentProps, ChartMetaFormField, PatientContext } from 'models/ui';
import { ChartActions, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSFlex, ELSFlexItem } from 'components/common/els';
import { FormField, SectionTitle } from './constants';
import DischargeSummaryScreenReadOnly from './DischargeSummaryScreenReadOnly';

interface DischargeSummaryScreenViewProps {
  isLocked: boolean;
  fragment: ChartFragment;
  statusFragment: ChartFragment;
  chartActionsProps: ChartActionsComponentProps;
  formFieldMap: Map<string, ChartMetaFormField>;
  formSubmittedCount: number;
  patientContext: PatientContext;
}

const DischargeSummaryScreenView = ({ isLocked, fragment, chartActionsProps, formFieldMap, formSubmittedCount, statusFragment }: DischargeSummaryScreenViewProps) => {
  const referralsField = formFieldMap.get(FormField.REFERRALS_TO);

  const renderTextBoxField = (formField: FormField) => <ErrorFormField formField={formFieldMap.get(formField)} formSubmittedCount={formSubmittedCount} />;
  const renderDateField = (formField: FormField) => <ErrorFormField formField={formFieldMap.get(formField)} formSubmittedCount={formSubmittedCount} />;

  return (
    <div className="discharge-summary-screen">
      {isLocked ? (
        <DischargeSummaryScreenReadOnly chartFragment={fragment} statusFragment={statusFragment} />
      ) : (
        <>
          <h1>Discharge Summary</h1>

          <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} />

          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderDateField(FormField.ADMISSION_DATE)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderDateField(FormField.DISCHARGE_DATE)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.REASON_FOR_ADMISSION}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.PRESENTING_PROBLEM)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.PRINCIPAL_DIAGNOSIS)}
            </ELSFlexItem>
          </ELSFlex>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.ADDITIONAL_DIAGNOSIS)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.SUMMARY_OF_HOSPITAL_STAY)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.TREATING_TEAM_AND_CONTACT_DETAILS}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.ATTENDING_CLINICIANS)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.WARD_UNIT)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.CLINICAL_SUMMARY}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.KEY_INVESTIGATIONS_AND_RESULTS)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.PROCEDURES_PERFORMED)}
            </ELSFlexItem>
          </ELSFlex>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.RESPONSE_TO_TREATMENT)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.OUTSTANDING_ISSUES)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.MEDICATIONS}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.DISCHARGE_MEDICATIONS)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.CHANGES_FROM_ADMISSION_MEDICATIONS)}
            </ELSFlexItem>
          </ELSFlex>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.REASONS_FOR_CHANGES)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.ALLERGIES_AND_ADVERSE_DRUG_REACTIONS)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.FOLLOW_UP_AND_REFERRALS}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o1">{renderTextBoxField(FormField.APPOINTMENTS)}</ELSFlexItem>
          </ELSFlex>

          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              <ErrorFormField formField={referralsField} formSubmittedCount={formSubmittedCount} />
            </ELSFlexItem>
          </ELSFlex>

          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o1">{renderTextBoxField(FormField.REFERRALS_TO_OTHER)}</ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.PATIENT_EDUCATION_AND_INSTRUCTIONS}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.DISCHARGE_INSTRUCTIONS)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.WARNING_SIGNS_TO_WATCH_FOR)}
            </ELSFlexItem>
          </ELSFlex>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.CONTACT_INFORMATION_FOR_URGENT_CONCERNS)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.SUPPORT_SERVICES_AND_EQUIPMENT}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.HOME_CARE_SERVICES_ARRANGED)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.EQUIPMENT_PROVIDED)}
            </ELSFlexItem>
          </ELSFlex>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.TRANSPORT_ARRANGEMENTS)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.CONSENT_AND_COMMUNICATION}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.PATIENT_CARER_INVOLVEMENT_IN_DISCHARGE_PLANNING)}
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.INTERPRETER_SERVICES_USED)}
            </ELSFlexItem>
          </ELSFlex>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o2" md="1o1">
              {renderTextBoxField(FormField.CONSENT_FOR_INFORMATION_SHARING)}
            </ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.CLINICAL_HANDOVER}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o1">{renderTextBoxField(FormField.HANDOVER_TO_RECEIVING_HEALTHCARE_PROVIDER)}</ELSFlexItem>
          </ELSFlex>

          <SectionDivider />

          <h2 className="u-els-margin-bottom">{SectionTitle.CLINICIAN_SIGNATURE}</h2>
          <ELSFlex className="u-flex-grid-2x u-flex-gutter-3x" wrap left>
            <ELSFlexItem w="1o3" md="1o1">
              {renderTextBoxField(FormField.CLINICIAN_SIGNATURE_NAME)}
            </ELSFlexItem>
            <ELSFlexItem w="1o3" md="1o1">
              {renderTextBoxField(FormField.CLINICIAN_SIGNATURE_POSITION)}
            </ELSFlexItem>
            <ELSFlexItem w="1o3" md="1o1">
              {renderDateField(FormField.CLINICIAN_SIGNATURE_DATE)}
            </ELSFlexItem>
          </ELSFlex>

          <ChartPermission fragmentType={fragment?.fragmentType}>{({ allowSaveFragment }) => allowSaveFragment && <ChartActions {...chartActionsProps} />}</ChartPermission>
        </>
      )}
    </div>
  );
};

DischargeSummaryScreenView.displayName = 'DischargeSummaryScreenView';
export default DischargeSummaryScreenView;
